import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { rqKeys } from '@/react-query-keys'
import {
  apiRequestNew,
  broadcastLogoutSync,
  clearAuthSessionCookies,
  clearAuthTokens,
} from '@/helpers/request'
import { TOPUP_BALANCE_CHANGED_EVENT } from '@/helpers/payment/topup-balance-pending-session'
import { useAppStore } from '@/store'
import { getMs } from '@/helpers'
import type { T_UserCardBalanceRes, T_UserCardsListRes } from '@/types/responses'
import {
  VISITOR_MESSAGE_API_NOTICES,
  findVisitorMessageById,
  useMessagesStore,
} from '@/pages/messages'

type T_NotificationEvent = {
  time: number
  name: string
  data?: any
}

const lsKeyForSince = (userId: number) => `ERA_NOTIFICATIONS_SINCE_${userId}`

/**
 * Long-polling уведомлений бэкенда:
 * GET /api/v1/client/notification/{userId}/{since}
 *
 * Используется для точечной инвалидации данных вместо частых polling запросов.
 */
export const useBackendNotifications = () => {
  const queryClient = useQueryClient()

  const setMainAccountBalanceFromNotification = useAppStore((s) => s.setMainAccountBalanceFromNotification)
  const setToUnauthorized = useAppStore((s) => s.setToUnauthorized)
  const isAuthorized = useAppStore((s) => s.isAuthorized)
  const userId = useAppStore((s) => s.userData.id)
  const addUnreadMessage = useMessagesStore((s) => s.addUnreadMessage)
  const markMessagesAsRead = useMessagesStore((s) => s.markMessagesAsRead)
  const updateTopicActivity = useMessagesStore((s) => s.updateTopicActivity)
  const isMessageMine = useMessagesStore((s) => s.isMessageMine)

  const sinceRef = useRef<number>(0)
  const stopRef = useRef(false)
  const backoffMsRef = useRef<number>(0)
  const isWindowActiveRef = useRef<boolean>(true)
  const activeEmptyStreakRef = useRef<number>(0)
  const inactiveEmptyStreakRef = useRef<number>(0)
  const lastActionAtRef = useRef<Record<string, number>>({})
  const scheduledTimeoutIdRef = useRef<Record<string, number | null>>({})
  const pendingRunRef = useRef<Record<string, (() => void | Promise<void>) | null>>({})
  const balanceBurstSeqRef = useRef<number>(0)

  useEffect(() => {
    stopRef.current = false

    // Не запускаем для неавторизованных (можно расширить позже на userId=0 при необходимости)
    if (!isAuthorized || !userId) return

    // Инициализируем since из localStorage, чтобы после перезагрузки не перечитывать "старые" события
    const lsKey = lsKeyForSince(userId)
    const saved = Number(localStorage.getItem(lsKey) || 0)
    sinceRef.current = Number.isFinite(saved) && saved > 0 ? saved : 0

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

    // Ограничение частоты запросов:
    // - если нет событий, опрашиваем редко
    // - если события есть, на короткое время ускоряемся, чтобы быстро "догнать" очередь
    const FAST_POLL_MS = 500
    // В активном режиме при пустых ответах используем лестницу, чтобы не долбить API:
    // 15s → 30s (сброс при появлении событий)
    const IDLE_POLL_MS_ACTIVE_STEPS = [
      getMs('15s'),
      getMs('30s'),
    ]
    const IDLE_POLL_MS_INACTIVE_STEPS = [
      getMs('15s'),
      getMs('30s'),
      getMs('60s'),
      getMs('300s'),
    ]

    // Отслеживаем активность вкладки/окна (для снижения частоты запросов в фоне)
    isWindowActiveRef.current = document.visibilityState === 'visible'

    const onVisibilityChange = () => {
      const active = document.visibilityState === 'visible'
      isWindowActiveRef.current = active
      if (active) {
        activeEmptyStreakRef.current = 0
        inactiveEmptyStreakRef.current = 0
      }
    }

    window.addEventListener('focus', onVisibilityChange)
    window.addEventListener('blur', onVisibilityChange)
    document.addEventListener('visibilitychange', onVisibilityChange)

    const runCoalesced = async (
      actionKey: string,
      run: () => void | Promise<void>,
      opts?: {
        minIntervalActiveMs?: number
        minIntervalInactiveMs?: number
        force?: boolean
      }
    ) => {
      const now = Date.now()
      if (opts?.force) {
        const tid = scheduledTimeoutIdRef.current[actionKey]
        if (typeof tid === 'number') {
          clearTimeout(tid)
        }
        scheduledTimeoutIdRef.current[actionKey] = null
        pendingRunRef.current[actionKey] = null
        lastActionAtRef.current[actionKey] = now
        await run()
        return
      }

      const minMs = isWindowActiveRef.current
        ? (opts?.minIntervalActiveMs ?? getMs('30s'))
        : (opts?.minIntervalInactiveMs ?? getMs('60s'))

      const prev = lastActionAtRef.current[actionKey] ?? 0
      if (prev && now - prev < minMs) {
        // Throttle with trailing: если пришли слишком часто, не теряем событие,
        // а планируем один отложенный запуск на конец окна.
        pendingRunRef.current[actionKey] = run

        if (typeof scheduledTimeoutIdRef.current[actionKey] === 'number') {
          return
        }

        const remaining = Math.max(0, minMs - (now - prev))
        scheduledTimeoutIdRef.current[actionKey] = window.setTimeout(() => {
          scheduledTimeoutIdRef.current[actionKey] = null
          const pending = pendingRunRef.current[actionKey]
          pendingRunRef.current[actionKey] = null
          if (stopRef.current) return
          if (!pending) return
          lastActionAtRef.current[actionKey] = Date.now()
          void pending()
        }, remaining)

        return
      }

      lastActionAtRef.current[actionKey] = now
      await run()
    }

    const invalidateAndRefetchActive = async (
      actionKey: string,
      queryKey: unknown[],
      opts?: {
        urgent?: boolean
        exact?: boolean
      }
    ) => {
      await runCoalesced(actionKey, async () => {
        await queryClient.invalidateQueries({
          queryKey,
          exact: opts?.exact ?? true,
          // помечаем stale, но не запускаем refetch для неактивных запросов
          refetchType: 'none',
        })

        // ВАЖНО:
        // Для нотификаций хотим обновить UI сразу. Если у query выставлен staleTime > 0,
        // фильтр stale:true может не дать сделать refetch.
        const refetchOpts: any = {
          queryKey,
          exact: opts?.exact ?? true,
          type: 'active',
        }
        if (!opts?.urgent) {
          refetchOpts.stale = true
        }
        await queryClient.refetchQueries(refetchOpts)
      }, {
        // По умолчанию нотификации должны обновлять UI быстро.
        // При этом коалесим пачки событий, чтобы не спамить сетью.
        minIntervalActiveMs: opts?.urgent ? getMs('5s') : getMs('10s'),
        minIntervalInactiveMs: opts?.urgent ? getMs('30s') : getMs('60s'),
      })
    }

    const invalidateNews = () => {
      invalidateAndRefetchActive('newsList', [ rqKeys.newsList ], { urgent: true })
      invalidateAndRefetchActive('newsListAll', [ rqKeys.newsList, 'all' ], { urgent: true })
      invalidateAndRefetchActive('newsArticle', [ rqKeys.newsArticle ], { urgent: true })
    }

    const invalidateTickets = (requestId?: number) => {
      // Коалесим, чтобы при частых событиях не превращать обновления в "таймер"
      invalidateAndRefetchActive('ticketsList', [ rqKeys.ticketsList ], { urgent: true })

      if (!requestId) return

      const tid = String(requestId)

      // Инвалидируем всё, что связано с конкретным тикетом:
      // - сам диалог: [ticketConversation, "{id}"]
      // - превью последнего сообщения: [ticketConversation, "{id}", "lastMessage"]
      queryClient.invalidateQueries({
        queryKey: [ rqKeys.ticketConversation, tid ],
        exact: false,
      })

      // Для активного экрана диалога дополнительно инициируем refetch сразу
      queryClient.refetchQueries({
        queryKey: [ rqKeys.ticketConversation, tid ],
        type: 'active',
      })
    }

    const invalidateMessageTopicListByTopicId = (topicId?: number) => {
      const normalizedTopicId = Number(topicId)
      if (!Number.isFinite(normalizedTopicId) || normalizedTopicId <= 0) return

      invalidateAndRefetchActive(
        `visitorMessageTopicList:${normalizedTopicId}`,
        [ rqKeys.visitorMessageTopicList, normalizedTopicId ],
        { urgent: true }
      )
    }

    const invalidateMessagesCommon = () => {
      invalidateAndRefetchActive('visitorMessageTopics', [ rqKeys.visitorMessageTopics ], { urgent: true })
      invalidateAndRefetchActive('visitorMessageUnread', [ rqKeys.visitorMessageUnread ], { urgent: true, exact: false })
    }

    const handleVisitorMessageCreated = async (evt: T_NotificationEvent) => {
      const messageId = Number(evt?.data?.id)
      if (!Number.isFinite(messageId) || messageId <= 0) return

      try {
        const found = await findVisitorMessageById(messageId)
        if (!found) return

        const isMine = isMessageMine(userId, found.message)
        if (!isMine) {
          addUnreadMessage({
            ...found.message,
            topic: found.topic,
          })
        }

        updateTopicActivity(found.topic.id, found.message.timestamp)
        invalidateMessagesCommon()
        invalidateMessageTopicListByTopicId(found.topic.id)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[Notifications] failed to handle visitor message create', error)
      }
    }

    const handleVisitorMessageHadRead = (evt: T_NotificationEvent) => {
      const idsRaw = Array.isArray(evt?.data?.message_ids) ? evt.data.message_ids : []
      const messageIds = idsRaw
        .map((id: unknown) => Number(id))
        .filter((id: number) => Number.isFinite(id) && id > 0)

      if (!messageIds.length) return

      markMessagesAsRead(messageIds)
      invalidateMessagesCommon()
    }

    const invalidateBalance = (opts?: { urgent?: boolean }) => {
      // Коалесим, чтобы при частых событиях не делать сетевые запросы слишком часто
      runCoalesced('userBalance+transactions', async () => {
        await queryClient.invalidateQueries({
          queryKey: [ rqKeys.userBalance ],
          refetchType: 'none',
        })
        await queryClient.invalidateQueries({
          queryKey: [ rqKeys.transactionsKey ],
          refetchType: 'none',
        })

        const refetchBalanceOpts: any = {
          queryKey: [ rqKeys.userBalance ],
          type: 'active',
        }
        const refetchTxOpts: any = {
          queryKey: [ rqKeys.transactionsKey ],
          type: 'active',
        }
        if (!opts?.urgent) {
          refetchBalanceOpts.stale = true
          refetchTxOpts.stale = true
        }
        await queryClient.refetchQueries(refetchBalanceOpts)
        await queryClient.refetchQueries(refetchTxOpts)
      }, {
        // Денежные события должны обновлять UI быстро.
        minIntervalActiveMs: opts?.urgent ? getMs('5s') : getMs('10s'),
        minIntervalInactiveMs: opts?.urgent ? getMs('30s') : getMs('60s'),
      })
    }

    const invalidateUser = () => {
      invalidateAndRefetchActive('userData', [ rqKeys.userData ], { urgent: true })
    }

    const isTxVisibleInCache = (txId: number) => {
      if (!Number.isFinite(txId) || txId <= 0) return false
      const txRes: any = queryClient.getQueryData([ rqKeys.transactionsKey ])
      const list = txRes?.data?.list
      if (!Array.isArray(list)) return false
      return list.some((t: any) => Number(t?.id) === txId)
    }

    const refetchBalanceBurst = (evt: T_NotificationEvent) => {
      // OLD API баланс/транзакции могут "догонять" изменение позже уведомления.
      // Делаем несколько попыток, но ОСТАНАВЛИВАЕМСЯ как только увидим transaction_id в списке транзакций.
      const txId = Number(evt.data?.transaction_id)

      const mySeq = ++balanceBurstSeqRef.current

      const delays = [ 0, 1500, 4000, 8000 ]

      ;(async () => {
        for (const d of delays) {
          if (stopRef.current) return
          if (balanceBurstSeqRef.current !== mySeq) return // superseded by newer event

          // Если транзакция уже появилась в кэше — прекращаем burst
          if (isTxVisibleInCache(txId)) return

          if (d) await sleep(d)

          if (stopRef.current) return
          if (balanceBurstSeqRef.current !== mySeq) return

          // Рефетчим то, что реально влияет на баланс в UI
          await Promise.all([
            queryClient.refetchQueries({
              queryKey: [ rqKeys.userBalance ],
              type: 'active',
            }),
            queryClient.refetchQueries({
              queryKey: [ rqKeys.transactionsKey ],
              type: 'active',
            }),
          ])

          // После refetch снова проверяем условие остановки
          if (isTxVisibleInCache(txId)) return
        }
      })()
    }

    const handleEvent = (evt: T_NotificationEvent) => {
      switch (evt.name) {
      // Команда на обновление фронта
      case 'office/frontend/reload':
        window.location.reload()
        return

      // Тема (пока нет явного theme-query — перестрахуемся: обновим UI данные)
      case 'office/theme/apply':
        invalidateUser()
        return

      // Профиль/пользовательские данные
      case 'client/visitor/edit':
      case 'client/visitor/lang':
      case 'client/visitor/login':
      case 'client/visitor/change':
      case 'classic/visitor/status':
        invalidateUser()
        return
      case 'client/visitor/logout':
        // Форсируем локальный logout и рассылаем маркер синхронизации,
        // чтобы другие вкладки/контексты вышли без ожидания keepalive.
        clearAuthSessionCookies()
        clearAuthTokens()
        broadcastLogoutSync()
        setToUnauthorized()
        queryClient.clear()
        return

      // Баланс/транзакции
      case 'moneyin/request/create':
      case 'moneyin/request/status':
      case 'moneyout/request/create':
      case 'moneyout/request/status':
        invalidateBalance({ urgent: true })
        return
      case 'money/visitor/balance': {
        const data = evt.data || {}
        
        // 1. Проверяем валюту (обновляем только основной рублевый счет, currency_id=1)
        // Если currency_id нет или оно равно 1 — считаем это основным счетом.
        const isMainCurrency = !data.currency_id || Number(data.currency_id) === 1

        if (isMainCurrency) {
          const rawBalance = data.value
          if (rawBalance !== undefined && rawBalance !== null) {
            const val = Number(rawBalance)
            if (!Number.isNaN(val)) {
              // Мгновенное обновление UI (Revision Guard внутри Store защитит от старых API ответов)
              setMainAccountBalanceFromNotification(val)
            }
          }
        }

        invalidateBalance({ urgent: true })
        refetchBalanceBurst(evt)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(
            TOPUP_BALANCE_CHANGED_EVENT,
            {
              detail: {
                eventName: evt.name,
                eventTime: Number(evt.time) || Date.now(),
                payload: data,
              },
            }
          ))
        }
        return
      }
      case 'money/card/balance': {
        const data = evt.data || {}
        const cardId = Number(data.card_id)
        const rawBalance = data.balance
        const eventTimeTs = Number(evt.time) || Date.now()

        if (!Number.isFinite(cardId) || cardId <= 0) return
        if (rawBalance === undefined || rawBalance === null) return

        const parsedBalance = Number(rawBalance)
        if (Number.isNaN(parsedBalance)) return

        const userCardsQueryKey = [ rqKeys.userCards, userId ] as const
        const userCardsList = queryClient.getQueryData<T_UserCardsListRes>(userCardsQueryKey)
        const isKnownUserCard = Array.isArray(userCardsList)
          && userCardsList.some((card) => Number(card?.id) === cardId)

        if (!isKnownUserCard) return

        // // eslint-disable-next-line no-console
        // console.log('[Notifications] card balance applied', {
        //   notificationNumber: eventTimeTs,
        //   card_id: cardId,
        //   balance: String(rawBalance),
        // })

        const queryKey = [ rqKeys.userCardBalance, userId, cardId ] as const
        const revQueryKey = [ rqKeys.userCardBalanceRev, userId, cardId ] as const

        // Отменяем in-flight запросы баланса по этой карте, чтобы уменьшить окно гонки.
        void queryClient.cancelQueries({
          queryKey,
          exact: true,
        })

        // Время уведомления считаем источником истины по свежести данных.
        queryClient.setQueryData<number>(revQueryKey, eventTimeTs)

        queryClient.setQueryData<T_UserCardBalanceRes>(
          queryKey,
          {
            balance: String(rawBalance),
            is_actual: true,
          }
        )
        return
      }

      // Тикеты
      case 'ticket/request/message':
      case 'ticket/request/status': {
        const requestId = Number(evt.data?.request_id)
        invalidateTickets(Number.isFinite(requestId) ? requestId : undefined)
        return
      }

      case VISITOR_MESSAGE_API_NOTICES.create:
        void handleVisitorMessageCreated(evt)
        return

      case VISITOR_MESSAGE_API_NOTICES.hadRead:
        handleVisitorMessageHadRead(evt)
        return

      // Новости/портал
      case 'portal/article/visible':
        invalidateNews()
        return

      // Документы магазина
      case 'store/document/create':
      case 'store/document/payment':
        invalidateBalance({ urgent: true })
        // Также могут влиять на документы/контент
        invalidateAndRefetchActive('documents', [ rqKeys.documents ], { urgent: true })
        invalidateAndRefetchActive('documentContent', [ rqKeys.documentContent ], { urgent: true, exact: false })
        return

      default:
        return
      }
    }

    const loop = async () => {
      while (!stopRef.current) {
        // Если оффлайн — ждём и не долбим сеть
        if (!navigator.onLine) {
          await sleep(getMs('5s'))
          continue
        }

        // Backoff после ошибок
        if (backoffMsRef.current > 0) {
          await sleep(backoffMsRef.current)
        }

        const since = sinceRef.current || 0

        try {
          const res = await apiRequestNew<any>(
            'GET',
            `/client/notification/${userId}/${since}`,
          )

          // Сбрасываем backoff после успешного ответа
          backoffMsRef.current = 0

          // API может отдавать данные как:
          // 1) { list, time }
          // 2) { data: { list, time }, ... }
          // Поэтому разбираем оба варианта.
          const payload = res as any
          const nextTimeRaw =
            payload?.time ??
            payload?.data?.time ??
            payload?.result?.time

          const nextTime = Number(nextTimeRaw)

          if (Number.isFinite(nextTime) && nextTime > 0) {
            sinceRef.current = nextTime
            localStorage.setItem(lsKey, String(nextTime))
          }

          const listRaw =
            payload?.list ??
            payload?.data?.list ??
            payload?.result?.list

          const list = Array.isArray(listRaw) ? listRaw : []
          for (const evt of list) {
            handleEvent(evt)
          }

          // Если событий нет — не долбим бэкенд, ждём дольше.
          // Если событий есть — слегка ускоряемся, чтобы быстро обработать очередь.
          if (!list.length) {
            if (isWindowActiveRef.current) {
              inactiveEmptyStreakRef.current = 0
              const idx = Math.min(
                activeEmptyStreakRef.current,
                IDLE_POLL_MS_ACTIVE_STEPS.length - 1,
              )
              activeEmptyStreakRef.current += 1
              await sleep(IDLE_POLL_MS_ACTIVE_STEPS[idx])
            } else {
              activeEmptyStreakRef.current = 0
              const idx = Math.min(
                inactiveEmptyStreakRef.current,
                IDLE_POLL_MS_INACTIVE_STEPS.length - 1,
              )
              inactiveEmptyStreakRef.current += 1
              await sleep(IDLE_POLL_MS_INACTIVE_STEPS[idx])
            }
          } else {
            activeEmptyStreakRef.current = 0
            inactiveEmptyStreakRef.current = 0
            // В фоне не нужно "догонять" слишком агрессивно
            await sleep(isWindowActiveRef.current ? FAST_POLL_MS : getMs('5s'))
          }
        } catch (e) {
          // Экспоненциальный backoff 5s → 10s → 20s → 40s → 60s
          backoffMsRef.current = Math.min(
            backoffMsRef.current ? backoffMsRef.current * 2 : getMs('5s'),
            getMs('60s'),
          )
          // eslint-disable-next-line no-console
          console.warn('[Notifications] polling failed, retry in', backoffMsRef.current, e)
        }
      }
    }

    loop()

    return () => {
      stopRef.current = true
      window.removeEventListener('focus', onVisibilityChange)
      window.removeEventListener('blur', onVisibilityChange)
      document.removeEventListener('visibilitychange', onVisibilityChange)

      // cleanup scheduled trailing runs
      for (const key of Object.keys(scheduledTimeoutIdRef.current)) {
        const tid = scheduledTimeoutIdRef.current[key]
        if (typeof tid === 'number') clearTimeout(tid)
      }
    }
  }, [
    isAuthorized,
    userId,
    queryClient,
    addUnreadMessage,
    markMessagesAsRead,
    updateTopicActivity,
    isMessageMine,
  ])
}

